import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import { DataAPIContext } from '../../api';
import { ContextStore } from '../../store/contextStore';
import ReviewDetail, { KIND_META } from './ReviewDetail';
import SavedAnswers from './SavedAnswers';
import { CHANNEL_COPY, CHANNEL_LABELS, otherChannel } from './channelCopy';
import { TagScopeContext, buildTagScope, plural } from './tagScope';
import {
  OUTCOME_LABELS, PAGE_SIZE, getKnowledgeReviewApi, ignoreReasons, inboxOperatorFor, isLiveFor,
} from './knowledgeReviewApi';
import { teamDiffers } from './teamAnswers';
import { isWebWidgetAvailable } from '../webWidgetAvailable';

/*
 * AI training inbox. Operator staff go through questions the chatbot couldn't answer,
 * places where their team and their knowledge base disagree, and trained answers that may
 * be out of date -- one small decision per card. Operators think of this as "training" the
 * AI, so the copy says train/trained throughout.
 *
 * One inbox per channel: AI Suite shows `support`, the Web Chat Widget page shows `widget`
 * (each gated on its own permission scope, ai.* vs web_widget.*). A trained answer can be
 * shared with, or copied to, the other channel when the user can write to both. Plan:
 * cplanetbackend/aidocs/plans/2026-09-24-knowledge-review-inbox.md
 */

const KIND_FILTERS = [
  { value: '', label: 'All', countKey: 'open' },
  { value: 'gap', label: 'Unanswered', countKey: 'gap' },
  { value: 'conflict', label: 'Answers don’t match', countKey: 'conflict' },
  { value: 'stale', label: 'Check if still right', countKey: 'stale' },
];

const SUCCESS_MESSAGES = {
  approve: 'Trained. Your chatbot will use this answer from now on.',
  keep: 'Thanks, marked as still right.',
  keepConflict: 'Done. The bot will use your knowledge base answer.',
  removeStaleAnswer: 'Answer removed. The bot will stop using it.',
};

function ItemRow({ item, selected, onClick }) {
  const meta = KIND_META[item.kind];
  const Icon = meta.icon;
  const sub = item.kind === 'stale'
    ? (item.stale_reason === 'expired' ? 'Expired' : 'Not checked in a while')
    : `Asked ${item.ask_count}× · ${moment(item.last_asked_ts).fromNow()}`;
  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      sx={{ alignItems: 'flex-start', py: 1.25, borderBottom: 1, borderColor: 'divider' }}
    >
      <Icon fontSize="small" color={meta.color} sx={{ mt: '3px', mr: 1.25, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.question}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {meta.short} · {sub}
        </Typography>
        {teamDiffers(item) && (
          <Box sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<GroupsOutlinedIcon />}
              label="Team disagrees"
              sx={{ height: 20, fontSize: 11, '& .MuiChip-icon': { fontSize: 14 } }}
            />
          </Box>
        )}
      </Box>
    </ListItemButton>
  );
}

function CaughtUp({ filtered, hint, onClearFilters }) {
  if (filtered) {
    return (
      <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No questions match these filters.</Typography>
        <Button size="small" onClick={onClearFilters}>Clear filters</Button>
      </Box>
    );
  }
  return (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
      <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
      <Typography variant="h6">Nothing to train right now</Typography>
      <Typography variant="body2" color="text.secondary">{hint}</Typography>
    </Box>
  );
}

// "The top 3 questions account for half of ..." -- sizes the job by impact rather than by
// a raw count, and explains why the list is ordered the way it is.
function ImpactLine({ summary }) {
  if (!summary?.asks_total) return null;
  const n = summary.half_of_asks_top_n;
  const days = summary.window_days;
  const concentrated = summary.asked_questions >= 4 && n <= summary.asked_questions / 2;
  const text = concentrated
    ? `The top ${n === 1 ? 'question accounts' : `${n} questions account`} for half of the ${summary.asks_total} times your chatbot couldn’t answer in the last ${days} days. Start at the top.`
    : `${summary.asked_questions} ${summary.asked_questions === 1 ? 'question' : 'questions'}, asked ${summary.asks_total} times in the last ${days} days.`;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      <TrendingUpIcon fontSize="small" color="primary" />
      <Typography variant="body2" color="text.secondary">{text}</Typography>
    </Stack>
  );
}

function DoneList({ items, channel, canWrite, onUndo }) {
  if (!items.length) {
    return <Typography variant="body2" color="text.secondary">Questions you handle will be listed here for a while, in case you change your mind.</Typography>;
  }
  const reasonLabel = (r) => ignoreReasons(channel).find((x) => x.value === r)?.label;
  const outcome = (item) => {
    if (item.ignore_reason === 'removed') return 'Answer removed';
    return OUTCOME_LABELS[item.status];
  };
  return (
    <Paper variant="outlined" sx={{ borderRadius: '12px' }}>
      {items.map((item, i) => (
        <Stack
          key={item.id}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          sx={{ p: 1.5, borderTop: i ? 1 : 0, borderColor: 'divider' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.question}</Typography>
            <Typography variant="caption" color="text.secondary">
              {item.resolved_by} · {moment(item.resolved_ts).fromNow()}
              {item.ignore_reason && reasonLabel(item.ignore_reason) ? ` · ${reasonLabel(item.ignore_reason)}` : ''}
            </Typography>
          </Box>
          <Chip size="small" label={outcome(item)} />
          {canWrite && <Button size="small" onClick={() => onUndo(item)}>Undo</Button>}
        </Stack>
      ))}
    </Paper>
  );
}

// Nearest scrolling ancestor, or null when the window scrolls (both current hosts).
const scrollParentOf = (el) => {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
};

/*
 * Layout: ONE scrollbar for the card. The detail pane has no height cap and scrolls with
 * the page (its action bar sits at the end of the card); the question list is sticky at
 * the top and only scrolls itself when it's taller than the screen.
 * `stickyTop` / `listMaxHeight` describe the host page's scroll area. Both hosts (AI Suite,
 * Web Chat Widget) scroll the window under the fixed 64px app bar -- the defaults. Never put
 * this inside a height-capped scrolling panel: that brings back the double scrollbar and
 * leaves dead space under the page.
 */
export default function KnowledgeReviewTab({
  channel, licenseeId, autoReplyOn, onOpenCountChange, stickyTop = 80, listMaxHeight = 'calc(100vh - 112px)',
  widgetId,
}) {
  const dataAPI = useContext(DataAPIContext);
  const {
    hasPerm, tourOperator, isFranchise, licensees,
  } = useContext(ContextStore);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));

  const live = isLiveFor(inboxOperatorFor(tourOperator, isFranchise, licensees, licenseeId));
  const api = useMemo(
    () => getKnowledgeReviewApi(dataAPI, licenseeId, live, widgetId),
    [dataAPI, licenseeId, live, widgetId],
  );
  const copy = CHANNEL_COPY[channel];
  const other = otherChannel(channel);
  // Each inbox is gated on its own scope; sharing/copying into the other channel needs
  // write there too (and the widget has to exist for the operator).
  const scopeFor = (ch) => (ch === 'widget' ? 'web_widget' : 'ai');
  // The sample preview shows sharing with the widget even where the operator has no widget.
  const widgetAvailable = isWebWidgetAvailable(tourOperator) || !live;
  const canWrite = hasPerm(scopeFor(channel), 'write');
  const canShare = canWrite && widgetAvailable && hasPerm(scopeFor(other), 'write');

  const [view, setView] = useState('review');
  const [kind, setKind] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // The product filter: a product, a System tag or an Access tag ({ kind, id, name }).
  const [tour, setTour] = useState(null);
  const [includeOnce, setIncludeOnce] = useState(false);
  // "Team answers differ": staff gave different answers at the same time -- the cards a
  // manager should clear first.
  const [differs, setDiffers] = useState(false);
  const [items, setItems] = useState([]);
  const [itemCount, setItemCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [doneItems, setDoneItems] = useState([]);
  const [answers, setAnswers] = useState([]);
  // Trained answers and History show once their own list arrives, not the queue's.
  const [answersLoaded, setAnswersLoaded] = useState(false);
  const [doneLoaded, setDoneLoaded] = useState(false);
  const [summary, setSummary] = useState(null);
  // The products/ response: { results, featured, other_tags, access_tags } (tagScope.js).
  const [catalog, setCatalog] = useState(null);
  const products = useMemo(() => catalog?.results || [], [catalog]);
  const scope = useMemo(() => buildTagScope(catalog), [catalog]);
  // Featured categories first, then every other tag category, Access tags, and single products.
  const filterOptions = useMemo(() => {
    const groupOf = (name) => plural(name).replace(/^./, (c) => c.toUpperCase());
    const tags = [
      ...scope.featured.flatMap((f) => f.tags.map((t) => ({ ...t, kind: 'tag', group: groupOf(f.name) }))),
      ...scope.otherTags.map((t) => ({ ...t, kind: 'tag', group: groupOf(t.category.name) })),
      ...scope.accessTags.map((a) => ({ ...a, kind: 'atag', group: 'Access tags' })),
    ];
    const tours = products.map((p) => ({ ...p, kind: 'product', group: 'Products' }));
    return tags.length ? [...tags, ...tours] : tours;
  }, [scope, products]);
  const filterTagKinds = [
    ...scope.featured.map((f) => f.name.toLowerCase()),
    ...(scope.otherTags.length || scope.accessTags.length ? ['tag'] : []),
  ];
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filtered = !!(kind || search || tour || differs);
  const clearFilters = () => { setKind(''); setSearchInput(''); setSearch(''); setTour(null); setDiffers(false); };

  // The tab badge always counts the default view (one-offs hidden), even while they're shown.
  const applySummary = useCallback((s) => {
    setSummary(s);
    onOpenCountChange?.(includeOnce ? s.open - s.one_off : s.open);
  }, [includeOnce, onOpenCountChange]);

  const refreshSummary = useCallback(async () => {
    applySummary(await api.summary(channel, { includeOnce }));
  }, [api, channel, includeOnce, applySummary]);

  // Page 1 of the open list, at least `size` long: "Show more" and reloads after an action
  // re-request everything shown so far, so resolved cards can't shift the paging.
  // A search looks through one-offs too: whoever searches wants THAT question, however
  // rarely it was asked.
  const fetchOpen = useCallback((size = PAGE_SIZE) => api.listItems({
    status: 'open',
    kind,
    channel,
    search,
    tour: tour?.kind === 'product' ? tour.id : undefined,
    tag: tour?.kind === 'tag' ? tour.id : undefined,
    atag: tour?.kind === 'atag' ? tour.id : undefined,
    teamDiffers: differs,
    includeOnce: includeOnce || !!search,
    page: 1,
    pageSize: size,
  }), [api, kind, channel, search, tour, differs, includeOnce]);

  const loadItems = useCallback(async (size) => {
    const { results, count: total } = await fetchOpen(size);
    setItems(results);
    setItemCount(total);
    return results;
  }, [fetchOpen]);

  // Once per inbox: products, trained answers, done list -- each shown as soon as it arrives, so
  // a slow one doesn't hold up the others (the trained-answers count sat at 0 for 30-60 s while
  // the product list loaded).
  useEffect(() => {
    let active = true;
    setAnswersLoaded(false);
    setDoneLoaded(false);
    // `loaded` is set even when the request fails, so a view never spins forever.
    const load = (request, apply, loaded) => request
      .then((data) => { if (active) apply(data); })
      .catch(() => { })
      .finally(() => { if (active && loaded) loaded(true); });
    // The sample data reads the featured categories from the operator's config, as the backend does.
    const config = tourOperator?.config || {};
    load(api.listProducts({
      featured: config.ai_featured_tag_categories || (config.ai_collection_category ? [config.ai_collection_category] : []),
    }), setCatalog);
    load(api.listAnswers(channel), setAnswers, setAnswersLoaded);
    load(api.listItems({ status: 'done', channel }), (done) => setDoneItems(done.results), setDoneLoaded);
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, channel]);

  // The queue: reloads whenever a filter changes.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [{ results, count: total }, s] = await Promise.all([fetchOpen(), api.summary(channel, { includeOnce })]);
        if (!active) return;
        setItems(results);
        setItemCount(total);
        applySummary(s);
        setSelectedId((cur) => (results.some((i) => i.id === cur) ? cur : (narrow ? null : results[0]?.id ?? null)));
      } catch (e) {
        if (active) setError(e?.message || 'Couldn’t load questions.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOpen]);

  const showMore = async () => {
    setLoadingMore(true);
    try {
      await loadItems(items.length + PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  };

  const selected = items.find((i) => i.id === selectedId) || null;

  // A new card starts at its top: after Train/Skip the page may still be scrolled to the
  // bottom of the previous (longer) card.
  const detailRef = useRef(null);
  useEffect(() => {
    const el = detailRef.current;
    if (!el?.scrollIntoView) return;
    const parent = scrollParentOf(el);
    const visibleTop = (parent ? parent.getBoundingClientRect().top : 0) + (parent ? 0 : stickyTop);
    if (el.getBoundingClientRect().top < visibleTop) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [selectedId, stickyTop]);

  // The card after `id` in the current order, wrapping; null when it was the last one.
  const nextAfter = (list, id) => {
    const idx = list.findIndex((i) => i.id === id);
    const rest = list.filter((i) => i.id !== id);
    if (!rest.length) return null;
    return rest[Math.min(Math.max(idx, 0), rest.length - 1)].id;
  };

  const refreshAfterChange = async () => {
    const [list, ans, done] = await Promise.all([
      loadItems(Math.max(PAGE_SIZE, items.length)),
      api.listAnswers(channel),
      api.listItems({ status: 'done', channel }),
    ]);
    setAnswers(ans);
    setDoneItems(done.results);
    await refreshSummary();
    return list;
  };

  const undo = async (item) => {
    await api.reopen(item.id);
    await refreshAfterChange();
    setView('review');
    setSelectedId(item.id);
  };

  const onAction = async (item, action, arg) => {
    const upcoming = narrow ? null : nextAfter(items, item.id);
    try {
      await api[action](item.id, arg);
    } catch (e) {
      enqueueSnackbar(`Couldn’t save that: ${e?.message || 'please try again'}`, { variant: 'error' });
      return;
    }
    await refreshAfterChange();
    setSelectedId(upcoming);
    const messageKey = action === 'keep' && item.kind === 'conflict' ? 'keepConflict' : action;
    const channelMessages = { ignore: copy.ignoreDone };
    enqueueSnackbar(channelMessages[action] || SUCCESS_MESSAGES[messageKey], {
      variant: 'success',
      action: (key) => (
        <Button color="inherit" size="small" onClick={() => { closeSnackbar(key); undo(item); }}>Undo</Button>
      ),
    });
  };

  const skip = () => setSelectedId(nextAfter(items, selectedId) ?? selectedId);

  // Trained-answer actions. Each reloads this channel's list and confirms in a snackbar.
  // Resolves false on failure, so a form (e.g. "Add a trained answer") can stay open with
  // what was typed.
  const answerAction = async (fn, message) => {
    try {
      await fn();
    } catch (e) {
      enqueueSnackbar(`Couldn’t save that: ${e?.message || 'please try again'}`, { variant: 'error' });
      return false;
    }
    setAnswers(await api.listAnswers(channel));
    enqueueSnackbar(message, { variant: 'success' });
    return true;
  };
  const otherLabel = CHANNEL_LABELS[other];
  const answerHandlers = {
    onCreate: (payload) => answerAction(
      () => api.createAnswer(payload),
      'Added. Your chatbot will use this answer from now on.',
    ),
    onSave: (id, payload, message) => answerAction(() => api.updateAnswer(id, payload), message || 'Answer updated.'),
    onShare: (a) => answerAction(
      () => api.setAnswerChannels(a.id, [channel, other]),
      `Shared. The ${otherLabel} uses this answer too, and edits apply to both.`,
    ),
    onUnshare: (a) => answerAction(
      () => api.setAnswerChannels(a.id, [channel]),
      `Stopped sharing. The ${otherLabel} no longer uses this answer.`,
    ),
    onCopy: (a) => answerAction(
      () => api.copyAnswer(a.id, other),
      `Copied to the ${otherLabel}. Changes to either copy won’t affect the other.`,
    ),
    // Shared answer being edited: give the other channel its own copy of the current
    // version, then un-share, so the edit only lands here.
    onSplit: (a) => answerAction(async () => {
      await api.copyAnswer(a.id, other);
      await api.setAnswerChannels(a.id, [channel]);
    }, `The ${otherLabel} now has its own copy. Your changes here won’t affect it.`),
    // Shared: stop using it here only; otherwise delete.
    onRemove: (a) => (a.channels.includes(other)
      ? answerAction(() => api.setAnswerChannels(a.id, [other]), `Removed from the ${CHANNEL_LABELS[channel]}. The ${otherLabel} still uses it.`)
      : answerAction(() => api.deleteAnswer(a.id), 'Answer removed.')),
  };

  const count = (key) => summary?.[key] ?? 0;

  // Unfiltered, the list is in impact order, so mark where the top half of all asks ends.
  const halfMark = !filtered ? summary?.half_of_asks_top_n : null;
  const showHalfMark = halfMark > 0 && halfMark < items.length && summary?.asked_questions >= 4;
  const oneOffToggle = (!kind || kind === 'gap') && (includeOnce || summary?.one_off > 0);

  const listPane = (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...(narrow ? {} : { position: 'sticky', top: stickyTop, maxHeight: listMaxHeight }),
      }}
    >
      <Stack spacing={1} sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Search questions"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          inputProps={{ 'aria-label': 'Search questions' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => { setSearchInput(''); setSearch(''); }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Autocomplete
          size="small"
          options={filterOptions}
          value={tour}
          onChange={(e, v) => setTour(v)}
          groupBy={filterTagKinds.length ? (o) => o.group : undefined}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id && a.kind === b.kind}
          renderOption={(props, o) => <li {...props} key={`${o.kind}${o.id}`}>{o.name}</li>}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={scope.featured.length === 1 ? `All ${plural(scope.featured[0].name.toLowerCase())}` : 'All products'}
              inputProps={{
                ...params.inputProps,
                'aria-label': `Filter by ${[...filterTagKinds, 'product'].join(', ').replace(/, ([^,]*)$/, ' or $1')}`,
              }}
            />
          )}
        />
      </Stack>
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {items.length === 0 ? (
          <CaughtUp filtered={filtered} hint={copy.emptyHint} onClearFilters={clearFilters} />
        ) : (
          <List disablePadding>
            {items.map((item, i) => (
              <React.Fragment key={item.id}>
                <ItemRow item={item} selected={item.id === selectedId} onClick={() => setSelectedId(item.id)} />
                {showHalfMark && i === halfMark - 1 && (
                  <Typography
                    variant="caption"
                    color="primary"
                    component="div"
                    sx={{ px: 2, py: 0.75, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}
                  >
                    ↑ These {halfMark} cover half of all the questions
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </List>
        )}
        {(items.length < itemCount || oneOffToggle) && (
          <Stack spacing={0.5} alignItems="center" sx={{ py: 1.5, px: 1 }}>
            {items.length < itemCount && (
              <>
                <Typography variant="caption" color="text.secondary">Showing {items.length} of {itemCount}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={showMore}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={14} /> : null}
                >
                  Show {Math.min(PAGE_SIZE, itemCount - items.length)} more
                </Button>
              </>
            )}
            {oneOffToggle && (
              <Button size="small" color="inherit" onClick={() => setIncludeOnce((v) => !v)}>
                {includeOnce
                  ? 'Hide questions asked only once'
                  : `Show ${summary.one_off} ${summary.one_off === 1 ? 'question' : 'questions'} asked only once`}
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Paper>
  );

  const detailProps = {
    api,
    canWrite,
    autoReplyOn,
    canShare,
    productOptions: products,
    onAction,
    onSkip: skip,
  };

  const detailPane = selected ? (
    <Paper
      ref={detailRef}
      variant="outlined"
      sx={{ borderRadius: '12px', p: { xs: 2, md: 2.5 }, pb: 0, scrollMarginTop: `${stickyTop}px` }}
    >
      {narrow && (
        <Button startIcon={<ArrowBackIcon />} onClick={() => setSelectedId(null)} sx={{ mb: 1, px: 0 }}>
          All questions
        </Button>
      )}
      <ReviewDetail item={selected} {...detailProps} />
    </Paper>
  ) : null;

  const spinner = <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>;

  return (
    <TagScopeContext.Provider value={scope}>
      <Box>
        {!live && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
            Preview with sample data. Nothing you do here is saved.
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-end' }} spacing={1.5} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{copy.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>{copy.subtitle}</Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(e, v) => v && setView(v)}
            aria-label="Review view"
          >
            <ToggleButton value="review">To train{summary ? ` (${count('open')})` : ''}</ToggleButton>
            <ToggleButton value="answers">Trained answers ({answers.length})</ToggleButton>
            <ToggleButton value="done">History</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Each view waits for its own list only: the queue's first load used to hold up all three,
            so a click on "Trained answers (5)" could land on the queue's spinner. */}
        {view === 'answers' ? (
          answersLoaded ? (
            <SavedAnswers
              answers={answers}
              channel={channel}
              canWrite={canWrite}
              canShare={canShare}
              showChannels={widgetAvailable}
              handlers={answerHandlers}
              productOptions={products}
              api={api}
            />
          ) : spinner
        ) : view === 'done' ? (
          doneLoaded ? <DoneList items={doneItems} channel={channel} canWrite={canWrite} onUndo={undo} /> : spinner
        ) : loading && !summary ? spinner : (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 2 }}>
              {KIND_FILTERS.map((f) => (
                <Chip
                  key={f.value || 'all'}
                  label={`${f.label} ${count(f.countKey)}`}
                  color={kind === f.value ? 'primary' : 'default'}
                  variant={kind === f.value ? 'filled' : 'outlined'}
                  onClick={() => setKind(f.value)}
                />
              ))}
              {(differs || count('team_differs') > 0) && (
                <Chip
                  icon={<GroupsOutlinedIcon />}
                  label={`Team answers differ ${count('team_differs')}`}
                  color={differs ? 'warning' : 'default'}
                  variant={differs ? 'filled' : 'outlined'}
                  onClick={() => setDiffers((d) => !d)}
                  sx={{ ml: { md: 1 } }}
                />
              )}
            </Stack>

            <ImpactLine summary={summary} />

            {narrow ? (selected ? detailPane : listPane) : (
              <Grid container spacing={2}>
                <Grid item md={4}>{listPane}</Grid>
                <Grid item md={8}>{detailPane}</Grid>
              </Grid>
            )}
          </>
        )}
      </Box>
    </TagScopeContext.Provider>
  );
}
